// WebGL2 preview renderer. Mirrors cpuRenderer.renderTemplate, but places the
// artwork in the shader so moving, scaling and rotating the design is free.
//
// Placement (design-space pixels of the Smart Object content):
//   { cx, cy, width, height, rotation }   rotation in degrees, about the centre.

import { normalizeAppearance } from "./cpuRenderer.js";

const VERTEX = `#version 300 es
in vec2 pos;
out vec2 xy;
void main() {
  xy = pos * 0.5 + 0.5;
  gl_Position = vec4(pos.x, -pos.y, 0.0, 1.0);
}`;

// Offscreen passes keep texture orientation (no flip).
const VERTEX_FBO = `#version 300 es
in vec2 pos;
out vec2 xy;
void main() {
  xy = pos * 0.5 + 0.5;
  gl_Position = vec4(pos, 0.0, 1.0);
}`;

const BLEND_GLSL = `
float blendCh(int m, float b, float s) {
  if (m == 1) return b * s;
  if (m == 2) return b + s - b * s;
  if (m == 3) return b <= 0.5 ? 2.0 * b * s : 1.0 - 2.0 * (1.0 - b) * (1.0 - s);
  if (m == 4) return min(b, s);
  if (m == 5) return max(b, s);
  if (m == 6) return b == 0.0 ? 0.0 : (s >= 1.0 ? 1.0 : min(1.0, b / (1.0 - s)));
  if (m == 7) return b >= 1.0 ? 1.0 : (s <= 0.0 ? 0.0 : 1.0 - min(1.0, (1.0 - b) / s));
  if (m == 8) return s <= 0.5 ? 2.0 * b * s : 1.0 - 2.0 * (1.0 - b) * (1.0 - s);
  if (m == 9) {
    if (s <= 0.5) return b - (1.0 - 2.0 * s) * b * (1.0 - b);
    float d = b <= 0.25 ? ((16.0 * b - 12.0) * b + 4.0) * b : sqrt(b);
    return b + (2.0 * s - 1.0) * (d - b);
  }
  if (m == 10) return abs(b - s);
  if (m == 11) return b + s - 2.0 * b * s;
  if (m == 12) return clamp(b + s - 1.0, 0.0, 1.0);
  if (m == 13) return clamp(b + s, 0.0, 1.0);
  if (m == 14) return clamp(b + 2.0 * s - 1.0, 0.0, 1.0);
  if (m == 15) {
    if (s <= 0.5) return s <= 0.0 ? 0.0 : clamp(1.0 - (1.0 - b) / (2.0 * s), 0.0, 1.0);
    return s >= 1.0 ? 1.0 : clamp(b / (2.0 * (1.0 - s)), 0.0, 1.0);
  }
  if (m == 16) return s <= 0.5 ? min(b, 2.0 * s) : max(b, 2.0 * s - 1.0);
  if (m == 17) return clamp(b - s, 0.0, 1.0);
  if (m == 18) return s <= 0.0 ? 1.0 : clamp(b / s, 0.0, 1.0);
  return s;
}
float lum(vec3 c) { return dot(c, vec3(0.3, 0.59, 0.11)); }
vec3 clipColor(vec3 c) {
  float l = lum(c), n = min(min(c.r, c.g), c.b), x = max(max(c.r, c.g), c.b);
  if (n < 0.0) c = l + (c - l) * l / max(l - n, 1e-6);
  if (x > 1.0) c = l + (c - l) * (1.0 - l) / max(x - l, 1e-6);
  return c;
}
vec3 setLum(vec3 c, float l) { return clipColor(c + (l - lum(c))); }
float satOf(vec3 c) { return max(max(c.r, c.g), c.b) - min(min(c.r, c.g), c.b); }
vec3 setSat(vec3 c, float s) {
  float mx = max(max(c.r, c.g), c.b), mn = min(min(c.r, c.g), c.b);
  return mx > mn ? (c - mn) * s / (mx - mn) : vec3(0.0);
}
vec3 blendRGB(int m, vec3 b, vec3 s) {
  if (m == 0) return s;
  if (m == 19) return setLum(setSat(s, satOf(b)), lum(b));
  if (m == 20) return setLum(setSat(b, satOf(s)), lum(b));
  if (m == 21) return setLum(s, lum(b));
  if (m == 22) return setLum(b, lum(s));
  return vec3(blendCh(m, b.r, s.r), blendCh(m, b.g, s.g), blendCh(m, b.b, s.b));
}`;

const FRAGMENT = `#version 300 es
precision highp float;
in vec2 xy;
out vec4 outColor;
uniform sampler2D uvMap, coverage, art, below, lightGain, lightBias, postGain, postBias;
uniform vec2 designSize, artCenter, artSize, artTexSize;
uniform vec2 rot; // cos, sin
uniform float shadowStrength, highlightStrength, designOpacity, brightness, contrast;
uniform int blendMode, lightSigned, postSigned, hasPost, showGrid, mode, hasArt;
${BLEND_GLSL}
vec2 decodeUV(vec4 p) {
  vec4 b = floor(p * 255.0 + 0.5);
  return vec2(b.r * 256.0 + b.g, b.b * 256.0 + b.a) / 65535.0;
}
vec3 overlayGrid(vec3 color, vec2 uv, float m) {
  if (showGrid == 0 || m <= 0.0) return color;
  vec2 cell = uv * 12.0;
  vec2 d = min(fract(cell), 1.0 - fract(cell));
  vec2 w = max(fwidth(cell), vec2(0.002));
  vec2 line = 1.0 - smoothstep(w * 0.45, w * 1.2, d);
  return mix(color, vec3(0.90, 0.29, 0.12), max(line.x, line.y) * m * 0.8);
}
void main() {
  float m = texture(coverage, xy).r;
  vec2 uv = decodeUV(texture(uvMap, xy));
  vec3 color = texture(below, xy).rgb;
  if (mode == 1) { outColor = vec4(mix(vec3(0.07, 0.09, 0.11), vec3(uv, 0.0), step(0.001, m)), 1.0); return; }
  // Design pixel -> artwork texture coordinate (uniform control flow, so the
  // derivatives used for mip selection are well defined).
  vec2 p = uv * designSize - artCenter;
  vec2 local = vec2(rot.x * p.x + rot.y * p.y, -rot.y * p.x + rot.x * p.y);
  vec2 q = local / artSize + 0.5;
  // Clamp derivatives so surface edges do not select tiny mip levels.
  vec2 gx = dFdx(q) * artTexSize, gy = dFdy(q) * artTexSize;
  float lim = 48.0;
  if (length(gx) > lim) gx = normalize(gx) * lim;
  if (length(gy) > lim) gy = normalize(gy) * lim;
  vec4 d = textureGrad(art, q, gx / artTexSize, gy / artTexSize);
  if (any(lessThan(q, vec2(0.0))) || any(greaterThan(q, vec2(1.0)))) d = vec4(0.0);
  if (m > 0.0 && hasArt == 1) {
    float a = d.a * m * designOpacity;
    if (a > 0.0) {
      vec3 dc = clamp((d.rgb / d.a + brightness - 0.5) * contrast + 0.5, 0.0, 1.0);
      vec3 g = texture(lightGain, xy).rgb;
      vec3 b = texture(lightBias, xy).rgb;
      if (lightSigned == 1) b = b * 2.0 - 1.0;
      vec3 shadow = max(vec3(0.0), 1.0 - g - b);
      vec3 adjusted = max(vec3(0.0), 1.0 - shadow * shadowStrength - b * highlightStrength);
      vec3 shaded = clamp(dc * adjusted + b * highlightStrength, 0.0, 1.0);
      color = mix(color, blendRGB(blendMode, color, shaded), a);
    }
  }
  if (hasPost == 1) {
    vec3 pb = texture(postBias, xy).rgb;
    if (postSigned == 1) pb = pb * 2.0 - 1.0;
    color = clamp(color * texture(postGain, xy).rgb + pb, 0.0, 1.0);
  }
  outColor = vec4(overlayGrid(color, uv, m), 1.0);
}`;

// Separable Gaussian blur on premultiplied colour.
const BLUR_FRAGMENT = `#version 300 es
precision highp float;
in vec2 xy;
out vec4 outColor;
uniform sampler2D src;
uniform vec2 step;
uniform float sigma;
uniform int radius;
void main() {
  vec4 acc = vec4(0.0);
  float total = 0.0;
  for (int i = -32; i <= 32; i++) {
    if (i < -radius || i > radius) continue;
    float w = exp(-0.5 * float(i * i) / (sigma * sigma));
    acc += texture(src, xy + step * float(i)) * w;
    total += w;
  }
  outColor = acc / total;
}`;

const COPY_FRAGMENT = `#version 300 es
precision highp float;
in vec2 xy;
out vec4 outColor;
uniform sampler2D src;
void main() { outColor = texture(src, xy); }`;

function compile(gl, type, source) {
  const s = gl.createShader(type);
  gl.shaderSource(s, source);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || "Shader compile failed");
  return s;
}

function program(gl, fragment, vertex = VERTEX) {
  const p = gl.createProgram();
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vertex));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fragment));
  gl.bindAttribLocation(p, 0, "pos");
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || "Program link failed");
  const uniforms = {};
  const count = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
  for (let i = 0; i < count; i++) {
    const info = gl.getActiveUniform(p, i);
    uniforms[info.name] = gl.getUniformLocation(p, info.name);
  }
  return { program: p, uniforms };
}

export function isWebGL2Available() {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl2");
    const ok = !!gl;
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
    return ok;
  } catch {
    return false;
  }
}

const TEXTURE_UNITS = { uvMap: 0, coverage: 1, below: 2, lightGain: 3, lightBias: 4, postGain: 5, postBias: 6, art: 7 };

export class MockupRenderer {
  constructor(canvas, { onContextLost, onContextRestored } = {}) {
    this.canvas = canvas;
    this.template = null;
    this.artSource = null;
    this.textures = {};
    this.blur = { key: null, texture: null };
    this.lost = false;
    this.handleLost = (e) => {
      e.preventDefault();
      this.lost = true;
      onContextLost?.();
    };
    this.handleRestored = () => {
      this.lost = false;
      this.init();
      if (this.template) this.setTemplate(this.template);
      if (this.artSource) this.setArtwork(this.artSource);
      onContextRestored?.();
    };
    canvas.addEventListener("webglcontextlost", this.handleLost);
    canvas.addEventListener("webglcontextrestored", this.handleRestored);
    this.init();
  }

  init() {
    const gl = this.canvas.getContext("webgl2", { premultipliedAlpha: false, preserveDrawingBuffer: false, antialias: false });
    if (!gl) throw new Error("WebGL2 is not available.");
    this.gl = gl;
    this.main = program(gl, FRAGMENT);
    this.blurProgram = program(gl, BLUR_FRAGMENT, VERTEX_FBO);
    this.copyProgram = program(gl, COPY_FRAGMENT, VERTEX_FBO);
    this.maxTexture = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.textures = {};
    this.blur = { key: null, texture: null, framebuffers: [] };
    this.identity = this.createTexture(1, 1, new Uint8Array([255, 255, 255, 255]), gl.NEAREST);
    this.zero = this.createTexture(1, 1, new Uint8Array([0, 0, 0, 255]), gl.NEAREST);
  }

  createTexture(width, height, data, filter, format) {
    const gl = this.gl, t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    if (format === "r8") gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, width, height, 0, gl.RED, gl.UNSIGNED_BYTE, data);
    else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
    return t;
  }

  /** Uploads a compiled template (see compiler.compileTemplate). */
  setTemplate(template) {
    if (this.lost) {
      this.template = template;
      return;
    }
    const gl = this.gl;
    for (const key of ["uvMap", "coverage", "below", "lightGain", "lightBias", "postGain", "postBias"]) {
      if (this.textures[key]) gl.deleteTexture(this.textures[key]);
    }
    const { width: w, height: h } = template;
    if (w > this.maxTexture || h > this.maxTexture) throw new Error("This mockup is too large for your graphics card.");
    const u8 = (a) => (a instanceof Uint8Array ? a : new Uint8Array(a.buffer, a.byteOffset, a.byteLength));
    this.textures.uvMap = this.createTexture(w, h, u8(template.uv), gl.NEAREST);
    this.textures.coverage = this.createTexture(w, h, u8(template.coverage), gl.NEAREST, "r8");
    this.textures.below = this.createTexture(w, h, u8(template.below), gl.NEAREST);
    this.textures.lightGain = this.createTexture(w, h, u8(template.lightGain), gl.NEAREST);
    this.textures.lightBias = this.createTexture(w, h, u8(template.lightBias), gl.NEAREST);
    this.textures.postGain = template.postGain ? this.createTexture(w, h, u8(template.postGain), gl.NEAREST) : null;
    this.textures.postBias = template.postBias ? this.createTexture(w, h, u8(template.postBias), gl.NEAREST) : null;
    this.template = template;
    this.canvas.width = w;
    this.canvas.height = h;
  }

  /**
   * Sets the design image. `source` is a canvas, ImageBitmap or image with
   * straight alpha; it is uploaded premultiplied and mipmapped.
   */
  setArtwork(source) {
    this.artSource = source;
    if (this.lost) return;
    const gl = this.gl;
    if (this.textures.art) gl.deleteTexture(this.textures.art);
    this.textures.art = null;
    this.blur.key = null;
    if (!source) return;
    const t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.generateMipmap(gl.TEXTURE_2D);
    this.textures.art = t;
    this.artSize = { width: source.width, height: source.height };
  }

  /** Returns a blurred copy of the artwork (cached by sigma), or the original. */
  blurredArt(sigmaTexels) {
    const gl = this.gl;
    if (!this.textures.art || !(sigmaTexels > 0.3)) return { texture: this.textures.art, size: this.artSize };
    // Downsample so the kernel stays small; blurred images lose nothing.
    let level = 0;
    while (sigmaTexels / 2 ** level > 8 && level < 6) level++;
    const sigma = sigmaTexels / 2 ** level;
    const w = Math.max(1, Math.round(this.artSize.width / 2 ** level)), h = Math.max(1, Math.round(this.artSize.height / 2 ** level));
    const key = `${w}x${h}:${sigma.toFixed(3)}`;
    if (this.blur.key === key) return { texture: this.blur.texture, size: this.artSize };

    const make = () => {
      const t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      // Plain LINEAR while rendering: a mipmap filter on a single-level
      // texture makes it incomplete, which samples as black.
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      const fb = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
      return { t, fb };
    };
    this.disposeBlur();
    const a = make(), b = make();
    gl.viewport(0, 0, w, h);
    // Downsample (mipmapped sampling) into A.
    gl.useProgram(this.copyProgram.program);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.art);
    gl.uniform1i(this.copyProgram.uniforms.src, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, a.fb);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    // Horizontal A -> B, then vertical B -> A.
    const radius = Math.min(32, Math.ceil(sigma * 3));
    gl.useProgram(this.blurProgram.program);
    const u = this.blurProgram.uniforms;
    gl.uniform1f(u.sigma, sigma);
    gl.uniform1i(u.radius, radius);
    gl.uniform1i(u.src, 0);
    gl.bindTexture(gl.TEXTURE_2D, a.t);
    gl.bindFramebuffer(gl.FRAMEBUFFER, b.fb);
    gl.uniform2f(u.step, 1 / w, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindTexture(gl.TEXTURE_2D, b.t);
    gl.bindFramebuffer(gl.FRAMEBUFFER, a.fb);
    gl.uniform2f(u.step, 0, 1 / h);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, a.t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.deleteFramebuffer(a.fb);
    gl.deleteFramebuffer(b.fb);
    gl.deleteTexture(b.t);
    this.blur = { key, texture: a.t };
    return { texture: a.t, size: this.artSize };
  }

  disposeBlur() {
    if (this.blur?.texture) this.gl.deleteTexture(this.blur.texture);
    this.blur = { key: null, texture: null };
  }

  /**
   * Draws the mockup.
   * @param placement  { cx, cy, width, height, rotation } in design pixels, or null
   * @param appearance see cpuRenderer.DEFAULT_APPEARANCE
   */
  draw({ placement = null, appearance = {}, grid = false, mode = 0 } = {}) {
    if (this.lost || !this.template) return;
    const gl = this.gl, t = this.template, a = normalizeAppearance(appearance);
    const hasArt = !!(placement && this.textures.art && placement.width > 0 && placement.height > 0);
    let art = { texture: this.zero, size: { width: 1, height: 1 } };
    if (hasArt) {
      // Blur is defined in Smart Object content pixels.
      const sigmaTexels = a.blur * (this.artSize.width / placement.width);
      art = this.blurredArt(sigmaTexels);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, t.width, t.height);
    gl.useProgram(this.main.program);
    const u = this.main.uniforms;
    const bind = (name, texture) => {
      gl.activeTexture(gl.TEXTURE0 + TEXTURE_UNITS[name]);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(u[name], TEXTURE_UNITS[name]);
    };
    bind("uvMap", this.textures.uvMap);
    bind("coverage", this.textures.coverage);
    bind("below", this.textures.below);
    bind("lightGain", this.textures.lightGain);
    bind("lightBias", this.textures.lightBias);
    bind("postGain", this.textures.postGain || this.identity);
    bind("postBias", this.textures.postBias || this.zero);
    bind("art", art.texture);
    const rad = ((placement?.rotation || 0) * Math.PI) / 180;
    gl.uniform2f(u.designSize, t.designWidth, t.designHeight);
    gl.uniform2f(u.artCenter, placement?.cx || 0, placement?.cy || 0);
    gl.uniform2f(u.artSize, placement?.width || 1, placement?.height || 1);
    gl.uniform2f(u.artTexSize, art.size.width, art.size.height);
    gl.uniform2f(u.rot, Math.cos(rad), Math.sin(rad));
    gl.uniform1f(u.shadowStrength, a.shadows);
    gl.uniform1f(u.highlightStrength, a.highlights);
    gl.uniform1f(u.designOpacity, a.opacity);
    gl.uniform1f(u.brightness, a.brightness / 100);
    gl.uniform1f(u.contrast, (100 + a.contrast) / 100);
    gl.uniform1i(u.blendMode, t.designBlend || 0);
    gl.uniform1i(u.lightSigned, t.lightBiasSigned ? 1 : 0);
    gl.uniform1i(u.postSigned, t.postBiasSigned ? 1 : 0);
    gl.uniform1i(u.hasPost, t.postGain ? 1 : 0);
    gl.uniform1i(u.showGrid, grid ? 1 : 0);
    gl.uniform1i(u.mode, mode);
    gl.uniform1i(u.hasArt, hasArt ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  /** Reads the current frame as RGBA bytes, top row first. Call right after draw(). */
  readPixels() {
    const gl = this.gl, { width: w, height: h } = this.canvas;
    const raw = new Uint8Array(w * h * 4), out = new Uint8Array(w * h * 4), row = w * 4;
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, raw);
    // GL rows are bottom-up.
    for (let y = 0; y < h; y++) out.set(raw.subarray((h - 1 - y) * row, (h - y) * row), y * row);
    return out;
  }

  dispose() {
    this.canvas.removeEventListener("webglcontextlost", this.handleLost);
    this.canvas.removeEventListener("webglcontextrestored", this.handleRestored);
    if (!this.gl || this.lost) return;
    const gl = this.gl;
    for (const t of Object.values(this.textures)) if (t) gl.deleteTexture(t);
    this.disposeBlur();
    gl.deleteTexture(this.identity);
    gl.deleteTexture(this.zero);
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    this.textures = {};
  }
}
